import { put, select } from 'redux-saga/effects';
import {
  signalingBreakoutRoomsReceived,
  signalingBreakoutRoomsStarted,
} from 'features/breakout-rooms/actions';
import { signalingRoomJoined } from 'features/room/actions';
import {
  breakoutTimerStartedAtSet,
  selectBreakoutRoomsStatus,
  selectBreakoutRoomTimer,
  selectBreakoutRoomTimerStartedAt,
} from 'features/breakout-rooms/breakoutRoomsSlice';
import { BreakoutRoomsStatus, BreakoutRoomsTimer } from 'features/breakout-rooms/types';

type AnchorTriggerAction =
  | ReturnType<typeof signalingBreakoutRoomsStarted>
  | ReturnType<typeof signalingRoomJoined>
  | ReturnType<typeof signalingBreakoutRoomsReceived>;

// Whether this event wrote a fresh running-timer base.
const establishesRunningTimer = (action: AnchorTriggerAction): boolean => {
  if (signalingBreakoutRoomsStarted.match(action)) {
    // No timer in the payload; the pre-configured one starts running now.
    return true;
  }

  if (signalingRoomJoined.match(action)) {
    return Boolean(action.payload.breakoutRooms?.timer);
  }

  return Boolean(action.payload.timer);
};

export function* anchorBreakoutTimerSaga(action: AnchorTriggerAction) {
  const status: BreakoutRoomsStatus = yield select(selectBreakoutRoomsStatus);
  const timer: BreakoutRoomsTimer | undefined = yield select(selectBreakoutRoomTimer);

  if (status !== 'started' || !timer) {
    return;
  }

  if (!establishesRunningTimer(action)) {
    return;
  }

  // started/roomJoined carry a live base, so always (re)anchor; other syncs only backfill a
  // missing anchor to avoid rewinding a running countdown.
  const reanchors =
    signalingBreakoutRoomsStarted.match(action) || signalingRoomJoined.match(action);

  if (!reanchors) {
    const existingStartedAt: number | null = yield select(selectBreakoutRoomTimerStartedAt);

    if (existingStartedAt !== null) {
      return;
    }
  }

  yield put(breakoutTimerStartedAtSet(Date.now()));
}
